import React, { FunctionComponent, useState } from 'react';
import { useForm } from 'react-hook-form';

import { SubmitButton } from 'components';
import {
  BuildingType,
  BUILDING_LEVEL_OPTIONS,
  YES_NO_OPTIONS,
} from 'scenes/common/types';

import SelectQuestion from 'scenes/common/components/Questions/SelectQuestion';
import { SegmentedControlQuestion } from 'scenes/common/components/Questions';

import FreestandingHouseModal from './FreestandingHouseModal';
import { AdditionalQuestionListType, BuildingTypeStepProps } from './types';

const BUILDING_TYPE = 'buildingType';

const BuildingTypeStep: FunctionComponent<BuildingTypeStepProps> = ({
  options,
  defaultStateValue,
  onNext,
  hideStrataQuestion,
  freestandingMetreGapValue,
  freestandingStoreyValue,
  terraceStoreyValue,
  terraceStrataValue,
  townhouseStoreyValue,
  townhouseStrataValue,
  unitStoreyValue,
  unitLevelValue,
  unitStrataValue,
  insuredItem,
}) => {
  const buildingTypeQuestion = `What type of building is the ${insuredItem}?`;

  const strataQuestion = {
    question: `Is your ${insuredItem} looked after by a body corporate or strata manager?`,
    questionOptions: YES_NO_OPTIONS,
  };

  const {
    formState: { isValid },
    handleSubmit,
    control,
    watch,
    errors,
  } = useForm({
    mode: 'onChange',
  });
  const [showModal, setShowModal] = useState(false);
  const watchedBuildingTypeOption: BuildingType = watch(
    BUILDING_TYPE,
    defaultStateValue
  );

  const additionalQuestions: AdditionalQuestionListType = {
    [BuildingType.FreestandingHouse]: [
      {
        question:
          'Is there a gap of a metre or more between the building and ground?',
        questionOptions: YES_NO_OPTIONS,
        displayModal: true,
        stateKey: 'freestandingMetreGap',
        defaultValue: freestandingMetreGapValue,
      },
      {
        question: 'Does the Freestanding house have more than one storey?',
        questionOptions: YES_NO_OPTIONS,
        stateKey: 'freestandingStorey',
        defaultValue: freestandingStoreyValue,
      },
    ],
    [BuildingType.Terrace]: [
      {
        question:
          'Does the Terrace / duplex / semi-detached have more than one storey?',
        questionOptions: YES_NO_OPTIONS,
        stateKey: 'terraceStorey',
        defaultValue: terraceStoreyValue,
      },
      {
        ...strataQuestion,
        stateKey: 'terraceStrata',
        defaultValue: terraceStrataValue,
        hidden: hideStrataQuestion,
      },
    ],
    [BuildingType.Townhouse]: [
      {
        question: 'Does the Townhouse / villa have more than one storey?',
        questionOptions: YES_NO_OPTIONS,
        stateKey: 'townhouseStorey',
        defaultValue: townhouseStoreyValue,
      },
      {
        ...strataQuestion,
        stateKey: 'townhouseStrata',
        defaultValue: townhouseStrataValue,
        hidden: hideStrataQuestion,
      },
    ],
    [BuildingType.Unit]: [
      {
        question: 'Does the Unit / flat / apartment have more than one storey?',
        questionOptions: YES_NO_OPTIONS,
        stateKey: 'unitStorey',
        defaultValue: unitStoreyValue,
      },
      {
        question: `On what level of the building is your ${insuredItem}?`,
        questionOptions: BUILDING_LEVEL_OPTIONS,
        displayDropdown: true,
        stateKey: 'unitLevel',
        defaultValue: unitLevelValue,
      },
      {
        ...strataQuestion,
        stateKey: 'unitStrata',
        defaultValue: unitStrataValue,
        hidden: hideStrataQuestion,
      },
    ],
    [BuildingType.Other]: [],
    [BuildingType.Unspecified]: [],
  };

  return (
    <form onSubmit={handleSubmit(onNext)}>
      <SelectQuestion
        question={buildingTypeQuestion}
        options={options}
        control={control}
        isLoading={false}
        stateKey={BUILDING_TYPE}
        defaultValue={defaultStateValue}
        ariaRequired
      />
      {additionalQuestions[watchedBuildingTypeOption]
        ?.filter(({ hidden }) => !hidden)
        .map(
          ({
            question,
            helpText,
            stateKey,
            defaultValue,
            questionOptions,
            displayModal,
            displayDropdown,
          }) =>
            displayDropdown ? (
              <SelectQuestion
                key={stateKey}
                question={question}
                helpText={helpText}
                options={questionOptions}
                control={control}
                isLoading={false}
                stateKey={stateKey}
                defaultValue={defaultValue as string}
              />
            ) : (
              <SegmentedControlQuestion
                key={stateKey}
                options={questionOptions}
                stateKey={stateKey}
                question={question}
                helpText={helpText}
                defaultValue={defaultValue}
                control={control}
                errors={errors}
                helpLink={
                  displayModal
                    ? {
                        id: 'answerYesModal',
                        text: 'When to answer "Yes"?',
                        ariaLabel: 'When to answer yes? Opens dialog',
                        onClick: () => setShowModal(true),
                      }
                    : undefined
                }
              />
            )
        )}
      <FreestandingHouseModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
      />
      <SubmitButton enabled={isValid}>Next</SubmitButton>
    </form>
  );
};

export default BuildingTypeStep;
